import React from 'react';
import { motion } from 'motion/react';
import { Link } from 'react-router-dom';
import { HomeShowcase } from '../components/HomeShowcase';
import { 
  Sparkles, 
  ArrowRight, 
  Brain, 
  Zap, 
  Shield, 
  Plus,
  Loader2,
  Calendar,
  Layout,
  Globe,
  Lock,
  ChevronDown,
  Play,
  FileText,
  Clock,
  CheckCircle2,
  MessageSquare,
  Instagram,
  Linkedin,
  Twitter,
  Facebook,
  Check
} from 'lucide-react';
import { useTranslation } from '../hooks/useTranslation';
import SEO from '../components/SEO';

interface HomeProps {
  onSignIn: () => void;
  user?: any;
  isSigningIn?: boolean;
  isAdmin?: boolean;
}

const Home: React.FC<HomeProps> = ({ onSignIn, user, isSigningIn, isAdmin }) => {
  const { t, isBrazil, region, currencySymbol, setRegion } = useTranslation();
  const [openFaq, setOpenFaq] = React.useState<number | null>(null);

  const regions = [
    { 
      id: 'za', name: t.southAfrica, currency: 'R', 
      solo_mo: 250, solo_yr: 2400,
      growth_mo: 550, growth_yr: 5500,
      agency_mo: 1450, agency_yr: 13500,
      discount: 100 
    },
    { 
      id: 'us', name: t.unitedStates, currency: '$', 
      solo_mo: 15, solo_yr: 145,
      growth_mo: 34, growth_yr: 330,
      agency_mo: 85, agency_yr: 900,
      discount: 6 
    },
    { 
      id: 'uk', name: t.unitedKingdom, currency: '£', 
      solo_mo: 12, solo_yr: 109,
      growth_mo: 29, growth_yr: 250,
      agency_mo: 69, agency_yr: 650,
      discount: 4.8 
    },
    { 
      id: 'br', name: t.brazil, currency: 'R$', 
      solo_mo: 55, solo_yr: 750,
      growth_mo: 129, growth_yr: 1200,
      agency_mo: 299, agency_yr: 2999,
      discount: 30 
    }
  ];

  const currentRegion = regions.find(r => r.id === region) || regions[1];
  const [promoCode, setPromoCode] = React.useState('');
  const isPromoValid = promoCode.toUpperCase() === 'WELCOME2026';

  const getPriceContent = (planId: 'trial' | 'solo' | 'growth' | 'agency', isPopular: boolean) => {
    if (planId === 'trial') {
      return <div className="text-4xl font-black">{currentRegion.currency}0</div>;
    }
    const basePrice = (currentRegion as any)[`${planId}_mo`];
    const symbol = currentRegion.currency;
    const formatted = `${symbol}${basePrice}`;

    if (isPromoValid) {
      const discounted = Math.max(0, basePrice - currentRegion.discount);
      const promoText = t.appliedPromo;
      return (
        <div className="flex flex-col">
          <div className="flex items-center gap-2">
            <span className={`text-xl line-through ${isPopular ? 'text-indigo-200' : 'text-gray-400'}`}>{formatted}</span>
            <span className="text-4xl font-black">{symbol}{discounted.toFixed(discounted % 1 === 0 ? 0 : 2)}</span>
          </div>
          <div className={`text-[10px] font-bold mt-1 ${isPopular ? 'text-yellow-300' : 'text-indigo-600'}`}>{promoText}</div>
        </div>
      );
    }

    return <div className="text-4xl font-black">{formatted}</div>;
  };

  const plans = [
    {
      id: 'trial' as const,
      name: t.freeTrial || "Free Trial",
      desc: t.trialDesc || "Test PlataPost risk-free to build your strategy.",
      features: t.plTrialFeatures || [
        "1 Brand Profile",
        "20 Free AI Tokens",
        "1 Active Plan",
        "No Credit Card Required"
      ],
      cta: t.startFreeTrial,
      popular: false
    },
    {
      id: 'solo' as const,
      name: "Solo",
      desc: t.soloDesc,
      features: t.plSoloFeatures,
      cta: t.startFree,
      popular: false
    },
    {
      id: 'growth' as const,
      name: "Growth",
      desc: t.growthDesc,
      features: t.plGrowthFeatures,
      cta: t.goGrowth,
      popular: true
    },
    {
      id: 'agency' as const,
      name: "Agency",
      desc: t.agencyDesc,
      features: t.plAgencyFeatures,
      cta: t.contactSales,
      popular: false
    }
  ];

  const faqs = [
    {
      q: t.faqWhatIsPlataPost,
      a: t.faqWhatIsPlataPostAns
    },
    {
      q: t.faqWhoIsPlataPostFor,
      a: t.faqWhoIsPlataPostForAns
    },
    {
      q: t.faqBrandVoice,
      a: t.faqBrandVoiceAns
    },
    {
      q: t.faqCanPlataPostWritePosts,
      a: t.faqCanPlataPostWritePostsAns
    },
    {
      q: t.faqCanIEditContent,
      a: t.faqCanIEditContentAns
    },
    {
      q: t.faqPlatforms,
      a: t.faqPlatformsAns
    }
  ];

  return (
    <div className="min-h-screen bg-brand-dark text-white font-sans selection:bg-indigo-500/30">
      <SEO 
        title={t.heroTitle}
        description={t.heroSubtitle}
      />

      {/* Hero Section */}
      <section className="relative pt-24 pb-24 px-6 overflow-hidden">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-3 gap-16 items-center relative z-10">
          <div className="text-left space-y-10 lg:col-span-1 relative z-20 pl-[9px] w-[381.725px]">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="hidden lg:inline-flex items-center gap-2 px-3 py-1.5 glass-card rounded-full font-bold text-indigo-400 border-indigo-500/20"
            >
              <Sparkles className="w-3.5 h-3.5" />
              <span className="uppercase tracking-[0.2em] text-[9px]">{t.aiVideoBadge}</span>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="space-y-6"
            >
              <h1 className="text-4xl lg:text-5xl font-black leading-[1.1] tracking-tighter flex flex-col">
                <span className="text-white uppercase">{t.heroSlogan1}</span>
                <span className="text-white uppercase">{t.heroSlogan2}</span>
                <span className="text-indigo-400 uppercase mt-2 text-[42px]">{t.heroSlogan3}</span>
                <span className="text-indigo-400 uppercase">{t.heroSlogan4}</span>
              </h1>
            </motion.div>
            
            <motion.p 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="text-sm text-gray-400 leading-relaxed font-medium max-w-sm pr-[50px]"
            >
              {t.heroSubtitle}
            </motion.p>
            
            {/* Mobile-only Image */}
            <div className="lg:hidden">
              <motion.div 
                initial={{ opacity: 0, scale: 0.95, y: 30 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                transition={{ duration: 0.8, ease: "easeOut" }}
              >
                <img 
                  src="/Platopost-hero-image.png" 
                  alt="PlataPost AI Social Media Master" 
                  className="w-full h-auto max-w-[500px] mx-auto drop-shadow-[0_0_30px_rgba(99,102,241,0.2)]"
                />
              </motion.div>
            </div>
            
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="flex flex-wrap items-center gap-4"
            >
              <button 
                onClick={onSignIn}
                className="px-6 h-[42px] bg-indigo-600 text-white rounded-xl font-bold text-base hover:bg-indigo-700 transition-all flex items-center justify-center gap-2 group shadow-[0_0_30px_rgba(99,102,241,0.2)] leading-[18px]"
              >
                {t.getStarted}
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </button>
              <button 
                onClick={onSignIn}
                className="px-4 h-[42px] border border-white/10 text-white rounded-xl font-bold text-base hover:bg-white/5 transition-all flex items-center justify-center leading-[10px]"
              >
                {t.startFreeTrial}
              </button>
            </motion.div>
          </div>

          {/* Desktop-only Image */}
          <div className="hidden lg:block relative lg:col-span-2 lg:-ml-32 lg:-mt-20">
            <motion.div 
              initial={{ opacity: 0, scale: 0.95, y: 30 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              transition={{ duration: 0.8, ease: "easeOut" }}
              className="relative z-10"
            >
              <img 
                src="/Platopost-hero-image.png" 
                alt="PlataPost AI Social Media Master" 
                className="w-full h-auto max-w-[950px] mx-auto pl-[45px] pt-[60px]"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Social Proof */}
      <section className="py-12 border-y border-white/[0.05] bg-white/[0.01]">
        <div className="max-w-7xl mx-auto px-6">
          <p className="text-center text-[10px] font-black text-gray-500 uppercase tracking-[0.4em] mb-12">
            {t.trustedBy}
          </p>
          <div className="flex flex-wrap justify-center items-center gap-x-16 gap-y-10 opacity-30 grayscale invert">
            <span className="text-2xl font-black italic tracking-tighter">NORTHWAY</span>
            <span className="text-2xl font-black tracking-tighter uppercase">cloudix</span>
            <span className="text-2xl font-bold flex items-center gap-2 lowercase tracking-tighter"><Brain className="w-8 h-8" /> kanba</span>
            <span className="text-2xl font-black italic tracking-tighter">lightbox</span>
            <span className="text-2xl font-black tracking-tighter lowercase">umbrella</span>
          </div>
        </div>
      </section>

      {/* Intelligent Command Center section */}
      <section className="py-0 px-6 relative overflow-hidden">
          <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-20 items-center">
              <div className="space-y-8 pl-[10px]">
                  <p className="text-indigo-500 font-bold uppercase tracking-widest text-sm">{t.builtForFounders}</p>
                  <h2 className="text-[42px] font-black leading-tight tracking-tighter">
                      {t.commandCenterTitle}
                  </h2>
                  <p className="text-gray-400 text-lg leading-relaxed max-w-xl">
                      {t.commandCenterDesc}
                  </p>
              </div>

              {/* Central Connection Graphic */}
              <div className="relative aspect-square max-w-lg mx-auto flex items-center justify-center">
                  <motion.div
                    initial={{ opacity: 0, scale: 0.9 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.8 }}
                    className="relative z-10 w-full"
                  >
                    <img 
                      src="/brand-brain-PNG.png" 
                      alt="Brand Brain Visualization" 
                      className="w-full h-auto mx-auto"
                    />
                  </motion.div>
              </div>
          </div>
      </section>

      {/* Features Grid (Bento style based on mockup) */}
      <section className="py-24 px-6 bg-[#030303]">
        <div className="max-w-7xl mx-auto space-y-12">
           <div className="text-center space-y-4">
              <p className="text-indigo-500 font-bold uppercase tracking-widest text-sm">{t.powerfulFeaturesKey}</p>
              <h2 className="text-4xl md:text-5xl font-black tracking-tight px-[80px]">{t.everythingNeeded}</h2>
           </div>
 
           <div className="grid md:grid-cols-3 gap-8">
              {[
                  {
                    icon: <Brain className="w-10 h-10 text-indigo-400" />,
                    title: t.aiStrategy,
                    desc: t.aiStrategyDesc
                  },
                  {
                    icon: <FileText className="w-10 h-10 text-blue-400" />,
                    title: t.instantGeneration,
                    desc: t.instantGenerationDesc
                  },
                  {
                    icon: <Shield className="w-10 h-10 text-emerald-400" />,
                    title: t.brandVoiceFeature,
                    desc: t.brandVoiceFeatureDesc
                  }
              ].map((feature, i) => (
                <div key={i} className="bento-card group">
                   <div className="w-20 h-20 bg-white/5 rounded-3xl flex items-center justify-center mb-8 border border-white/5 group-hover:scale-110 transition-transform">
                      {feature.icon}
                   </div>
                   <h3 className="text-2xl font-bold mb-4">{feature.title}</h3>
                   <p className="text-gray-400 leading-relaxed font-medium">{feature.desc}</p>
                </div>
              ))}
           </div>
        </div>
      </section>

      <HomeShowcase />

      {/* How it Works Section */}
      <section className="py-32 px-6 border-y border-white/[0.05]">
          <div className="max-w-7xl mx-auto space-y-20">
              <div className="text-center space-y-4">
                  <p className="text-indigo-500 font-bold uppercase tracking-widest text-sm">{t.howItWorks}</p>
                  <h2 className="text-4xl font-black tracking-tight">{t.featuresSubtitle}</h2>
              </div>
 
              <div className="grid lg:grid-cols-4 gap-12 relative">
                  {/* Connection line for desktop */}
                  <div className="hidden lg:block absolute top-[25%] left-[10%] right-[10%] h-[2px] bg-gradient-to-r from-transparent via-indigo-500/20 to-transparent" />
                  
                  {[
                      { step: 1, title: t.step1Title, desc: t.step1Desc, icon: <FileText className="w-8 h-8" /> },
                      { step: 2, title: t.step2Title, desc: t.step2Desc, icon: <Brain className="w-8 h-8" /> },
                      { step: 3, title: t.step3Title, desc: t.step3Desc, icon: <Sparkles className="w-8 h-8" /> },
                      { step: 4, title: t.step4Title, desc: t.step4Desc, icon: <Clock className="w-8 h-8" /> }
                  ].map((item, i) => (
                      <div key={i} className="relative space-y-6 text-center">
                          <div className="w-20 h-20 glass-card rounded-2xl flex items-center justify-center mx-auto z-10 relative">
                              <span className="absolute -top-3 -left-3 w-8 h-8 bg-indigo-600 rounded-lg flex items-center justify-center font-black text-xs shadow-lg">{item.step}</span>
                              <div className="text-indigo-400">{item.icon}</div>
                          </div>
                          <h3 className="text-xl font-bold">{item.title}</h3>
                          <p className="text-gray-500 text-sm font-medium leading-relaxed">{item.desc}</p>
                      </div>
                  ))}
              </div>
          </div>
      </section>

      {/* Pricing Section */}
      <section className="py-24 px-6 relative" id="pricing">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-6xl font-black text-white mb-6">
              {t.simplePricing.split(' ')[0]} <span className="text-indigo-500">{t.simplePricing.split(' ')[1]}</span>
            </h2>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto mb-10">
              {t.pricingSubtitle}
            </p>

            <div className="flex flex-col items-center gap-6">
              {isAdmin && (
                <div className="flex flex-col items-center gap-2">
                  <label className="text-[10px] font-black text-gray-500 uppercase tracking-widest">
                    {t.selectRegion}
                  </label>
                  <div className="inline-flex items-center gap-2 p-1.5 bg-white/5 rounded-2xl border border-white/10">
                    {regions.map((r) => (
                      <button
                        key={r.id}
                        onClick={() => setRegion(r.id as any)}
                        className={`px-4 py-2 text-sm font-bold rounded-xl transition-all ${
                          region === r.id
                            ? 'bg-white text-indigo-600 shadow-sm'
                            : 'text-gray-400 hover:text-gray-200'
                        }`}
                      >
                        {r.name}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex flex-col sm:flex-row items-center gap-4">
                <label className="text-xs font-black text-gray-500 uppercase tracking-widest">
                  {t.promoCodeLabel}
                </label>
                <div className="relative">
                  <input 
                    type="text"
                    placeholder="WELCOME2026"
                    value={promoCode}
                    onChange={(e) => setPromoCode(e.target.value)}
                    className="px-6 py-3 bg-white/5 border border-white/10 rounded-2xl text-sm font-bold focus:ring-2 focus:ring-indigo-600/20 focus:border-indigo-600 transition-all uppercase placeholder:text-gray-600 w-64 text-center text-white"
                  />
                  {isPromoValid && (
                    <div className="absolute -top-2 -right-2 bg-green-500 text-white p-1 rounded-full shadow-lg">
                      <Check className="w-3 h-3" />
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="grid md:grid-cols-4 gap-8 mb-20">
            {plans.map((plan) => (
              <div 
                key={plan.name}
                className={`relative rounded-[3rem] p-10 border transition-all hover:-translate-y-2 duration-300 ${
                  plan.popular 
                    ? 'bg-indigo-600 text-white border-indigo-600 shadow-2xl shadow-indigo-500/20' 
                    : 'bg-white/5 text-white border-white/10 shadow-sm'
                }`}
              >
                {plan.popular && (
                  <div className="absolute -top-5 left-1/2 -translate-x-1/2 px-4 py-1.5 bg-yellow-400 text-gray-900 text-xs font-black uppercase tracking-widest rounded-full">
                    {t.mostPopular}
                  </div>
                )}
                
                <div className="mb-8">
                  <h3 className={`text-2xl font-black mb-2 ${plan.popular ? 'text-white' : 'text-white'}`}>{plan.name}</h3>
                  <p className={`text-sm ${plan.popular ? 'text-indigo-100' : 'text-gray-400'}`}>{plan.desc}</p>
                </div>

                <div className="mb-8 h-20 flex items-center">
                  <div className="flex items-baseline gap-1">
                    {getPriceContent(plan.id, plan.popular || false)}
                    <span className={`text-sm font-bold ${plan.popular ? 'text-indigo-200' : 'text-gray-500'}`}>
                      {t.perMonth}
                    </span>
                  </div>
                </div>

                <ul className="space-y-4 mb-10 min-h-[300px]">
                  {plan.features.map((feature) => (
                    <li key={feature} className="flex items-start gap-3">
                      <div className={`mt-0.5 w-5 h-5 rounded-full flex items-center justify-center ${plan.popular ? 'bg-white/20' : 'bg-indigo-50/10'}`}>
                        <Check className={`w-3 h-3 ${plan.popular ? 'text-white' : 'text-indigo-400'}`} />
                      </div>
                      <span className="text-sm font-medium">{feature}</span>
                    </li>
                  ))}
                </ul>

                <button
                  onClick={onSignIn}
                  className={`w-full py-4 rounded-2xl font-black text-center transition-all flex items-center justify-center ${
                    plan.popular 
                      ? 'bg-white text-indigo-600 hover:bg-gray-50' 
                      : 'bg-indigo-600 text-white hover:bg-indigo-700 shadow-lg shadow-indigo-600/20'
                  }`}
                >
                  {plan.cta}
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section with Character */}
      <section className="pt-[60px] pb-32 px-6">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-20 items-center">
          <div className="space-y-12">
            <div className="pr-[100px]">
               <p className="text-indigo-500 font-bold uppercase tracking-widest text-sm mb-4">{t.faqTitle}</p>
               <h2 className="text-[42px] font-black tracking-tighter">{t.faqCharacterTitle}</h2>
            </div>

            <div className="space-y-4">
              {faqs.map((faq, i) => (
                <div 
                  key={i} 
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="group glass-card px-6 py-[10px] rounded-3xl border-white/[0.05] hover:border-indigo-500/30 transition-all cursor-pointer"
                >
                  <div className="flex justify-between items-center gap-4">
                     <h3 className="text-lg font-bold flex items-center gap-3">
                        {faq.q}
                     </h3>
                     <motion.div
                       animate={{ rotate: openFaq === i ? 180 : 0 }}
                       transition={{ duration: 0.3 }}
                     >
                       <ChevronDown className={`w-5 h-5 ${openFaq === i ? 'text-indigo-400' : 'text-gray-500'} group-hover:text-indigo-400 transition-colors`} />
                     </motion.div>
                  </div>

                  <motion.div
                    initial={false}
                    animate={{ 
                      height: openFaq === i ? 'auto' : 0,
                      opacity: openFaq === i ? 1 : 0,
                      marginTop: openFaq === i ? 16 : 0
                    }}
                    transition={{ duration: 0.3, ease: 'easeInOut' }}
                    className="overflow-hidden"
                  >
                    <p className="text-gray-400 leading-relaxed font-medium">
                      {faq.a}
                    </p>
                  </motion.div>
                </div>
              ))}
            </div>
          </div>

          <div className="relative hidden lg:block">
             <motion.div 
               initial={{ opacity: 0, x: 20 }}
               whileInView={{ opacity: 1, x: 0 }}
               viewport={{ once: true }}
               className="relative z-10 glass-card p-4 rounded-[3rem] border-white/5"
             >
                <img 
                  src="/PlataPost-Social-Media-Manager.png" 
                  alt="PlataPost Social Media Manager" 
                  className="w-full h-auto rounded-[2.5rem]"
                />
             </motion.div>
             <div className="absolute -bottom-10 -right-10 w-64 h-64 bg-indigo-600/20 rounded-full blur-[80px]" />
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-32 px-6">
        <div className="max-w-7xl mx-auto overflow-hidden rounded-[3.5rem] bg-indigo-600 relative group">
           <div className="px-12 py-[60px] md:py-[60px] text-center space-y-10 relative z-10">
              <h2 className="text-5xl md:text-7xl font-black tracking-tighter text-glow">
                {t.ctaTitle}
              </h2>
              <p className="text-indigo-100 text-xl max-w-2xl mx-auto font-medium">
                {t.ctaSubtitle}
              </p>
              <button 
                onClick={onSignIn}
                className="px-12 py-5 bg-white text-indigo-600 rounded-full font-black text-xl hover:scale-105 transition-all shadow-2xl shadow-black/20 inline-flex items-center gap-3 justify-center"
              >
                {t.startFreeTrial} 
                <ArrowRight className="w-6 h-6" />
              </button>
           </div>
           
           {/* Decorative elements */}
           <div className="absolute top-0 right-0 w-96 h-96 bg-white/10 rounded-full blur-[100px] -mr-48 -mt-48 group-hover:scale-110 transition-transform duration-700" />
           <div className="absolute bottom-0 left-0 w-96 h-96 bg-black/20 rounded-full blur-[100px] -ml-48 -mb-48" />
        </div>
      </section>
    </div>
  );
};

export default Home;
